import { ReactNode } from 'react';

import { ParsedUrlQuery } from 'querystring';

import { Specialties } from '@/features/listing/listing.types';
import { PhysicianData } from '@/features/profile/profile.types';
import { SitecorePageProps } from '@/lib/page-props';
import { Field, LinkFieldValue } from '@sitecore-jss/sitecore-jss-nextjs';

import { SearchAddressData } from './SearchAddressData';

export interface OASSitecoreComponentFields {
  coids?: string;
  dshsEnglish?: string;
  dshsSpanish?: string;
  hostTitle?: string;
  program?: string;
  siteType?: string;
  marketContext?: string;
  notFound?: boolean;
  pageTitle?: Field<string>;
  pageShortDescription?: Field<string>;
  bookingDsEnglish?: string;
  bookingDsSpanish?: string;
  physicianReferralPhoneNumberEnglish?: string;
  physicianReferralPhoneNumberSpanish?: string;
  physicianData?: PhysicianData;
  physicianFullName?: string;
  physicianMetaDescription?: string;
  physicianMetaTitle?: string;
  locationUrlTitle?: string;
  locationUrlTitleLabel?: string;
}

export interface OasAppProviderProps {
  pageProps: SitecorePageProps;
  query: ParsedUrlQuery;
  children: ReactNode;
}

export interface SitecoreFields {
  physiciansCount?: number;
  isMho?: boolean;
}

export interface DotCMSFields {
  dotCmsHostId: string;
  dotCmsHostName?: string;
  coreHostId?: string;
  mbcDefaultAccesspoint?: string;
  mbcLocationAccesspoint?: string;
}

export interface OasUnifiedAppModel {
  hostTitle: string;
  siteType: string;
  coids: string;
  notFound: boolean;
  program: string;
  isLocation: boolean;
  dshsEnglish?: string;
  dshsSpanish?: string;
  facilityCoid?: string;
  breadcrumbs: LinkFieldValue[];
  pageTitle?: Field<string>;
  pageShortDescription?: Field<string>;
  bookingDsEnglish?: string;
  bookingDsSpanish?: string;
  physicianReferralPhoneNumberEnglish?: string;
  physicianReferralPhoneNumberSpanish?: string;
  physicianData?: PhysicianData;
  physicianFullName?: string;
  physicianMetaDescription?: string;
  physicianMetaTitle?: string;
  locationUrlTitle?: string;
  locationUrlTitleLabel?: string;
  searchAddressData?: SearchAddressData;
  specialties: Specialties;
  scSiteCookie?: string;
  currentBookingInformation: { doctorUrlTitle: string | null };
}

export const HostApp = {
  dotcms: 'dotcms',
  mho: 'mho',
  mhonative: 'mhonative',
  sitecore: 'sitecore',
} as const;

export type Trans = <X extends unknown[] | Record<string, unknown>>(
  key: string | (string | number)[],
  params?: X | undefined,
  lang?: string
) => string;

export type HostApp = (typeof HostApp)[keyof typeof HostApp];

export type OasAppContextProps = OasUnifiedAppModel & {
  hostApp: HostApp;
  dotcms?: DotCMSFields;
  isMho?: boolean;
  sitecore?: SitecoreFields;
  scSiteCookie?: string;
};

export type GlobalAppState = Omit<
  OasAppContextProps,
  'pageTitle' | 'pageShortDescription' | 'breadcrumbs'
>;
export type PageState = Pick<
  OasAppContextProps,
  'pageTitle' | 'pageShortDescription' | 'breadcrumbs'
>;
